// Ported from formosan-reader/src/utils/audio.js (the hardened reference
// implementation) — do not hand-patch this file; fix there first, then re-port.
// Local addition on top of the port: the rAF highlight ticker (see below).
let currentAudio = null;
let stopFn = null;

// Inward trim applied to the end of a word clip (seconds).
export const WORD_TRIM = 0.04;

// Lead-in / tail-out so a segment/sentence/paragraph doesn't start or end abruptly.
export const PRE_ROLL = 0.15, POST_ROLL = 0.15;

export function stopAll() {
  if (stopFn) { stopFn(); stopFn = null; }
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }
  stopWordSrc();
}

// --- Word playback: Web Audio, sample-accurate ---------------------------
// HTMLAudioElement seeks land on AAC packet boundaries (±46ms at 22.05kHz)
// and pause() adds 20-80ms — through that player a word-tiled span like
// ka 3.180-3.351 plays ≈[3.13..3.40] = "skanii". Word taps therefore bypass
// seeking entirely: decode the clip once, play the exact sample range on a
// BufferSource with a scheduled stop. (Historic hand-dragged borders were
// ear-tuned THROUGH the quantizing player; acoustically-true data needs a
// sample-accurate one.)
let actx = null, wordSrc = null, wordToken = null;
const wordBufs = new Map();            // url -> Promise<AudioBuffer>
function wordBuffer(url) {
  if (!wordBufs.has(url)) {
    if (wordBufs.size > 24) wordBufs.clear();   // clips are small; tiny cache
    wordBufs.set(url, fetch(url).then(r => r.arrayBuffer()).then(b => actx.decodeAudioData(b)));
  }
  return wordBufs.get(url);
}
function stopWordSrc() {
  wordToken = null;
  if (wordSrc) { try { wordSrc.stop(); } catch (_) {} wordSrc = null; }
}

// Run `begin` once the element knows its duration, so we can clamp the seek.
function whenReady(audio, begin) {
  if (audio.readyState >= 1 && audio.duration) begin();
  else audio.addEventListener('loadedmetadata', begin, { once: true });
}

// Double-play rule (user spec): a loading clip must not start while ANOTHER
// clip is live — but an empty slot means nothing is playing, so re-register
// and start (a bare stopAll between request and metadata must not kill the
// play: that killed replay and left views stuck "playing"). Returns false
// when the caller must yield; yielding callers fire their end-callback so
// no UI state ever sticks.
function claimSlot(audio, restoreStopFn) {
  if (currentAudio === audio) return true;
  if (currentAudio) return false;          // a newer clip is live — yield
  currentAudio = audio;                    // slot empty — reclaim and play
  if (restoreStopFn) stopFn = restoreStopFn;
  return true;
}

// timeupdate is ~4 Hz — too coarse to light 0.12 s words ("ka" in Speaking
// Book line 1). This reports position every animation frame for HIGHLIGHTING
// ONLY; end/advance decisions stay on timeupdate exactly as in the reference.
function highlightTicker(tick) {
  let id;
  const loop = () => { tick(); id = requestAnimationFrame(loop); };
  id = requestAnimationFrame(loop);
  return () => cancelAnimationFrame(id);
}

export function playWord(url, wordStart, wordEnd) {
  stopAll();
  if (!actx) actx = new (window.AudioContext || window.webkitAudioContext)();
  if (actx.state === 'suspended') actx.resume();
  const me = {};
  wordToken = me;
  wordBuffer(url).then((buf) => {
    if (wordToken !== me) return;                       // superseded while decoding
    if (currentAudio && !currentAudio.paused) return;   // a range is live — yield
    // Fixed 0.04 chops a third off a 0.12s word ("ka") — trim proportionally.
    const trim = Math.min(WORD_TRIM, 0.15 * Math.max(0, wordEnd - wordStart));
    const start = Math.max(0, Math.min(wordStart, buf.duration));
    const dur = Math.max(0.02, Math.min(wordEnd - trim, buf.duration) - start);
    const s = actx.createBufferSource();
    s.buffer = buf;
    s.connect(actx.destination);
    s.onended = () => { if (wordSrc === s) wordSrc = null; };
    wordSrc = s;
    s.start(0, start, dur);                             // exact samples, scheduled stop
  }).catch(() => {});
}

export function playSentence(url, onTimeUpdate, onEnded) {
  stopAll();
  const audio = new Audio(url);
  currentAudio = audio;
  const handleTime = () => onTimeUpdate && onTimeUpdate(audio.currentTime);
  const stopTick = highlightTicker(() => {
    if (currentAudio === audio && !audio.paused) handleTime();
  });
  const handleEnd  = () => { stopTick(); stopFn = null; currentAudio = null; onEnded && onEnded(); };
  audio.addEventListener('timeupdate', handleTime);
  audio.addEventListener('ended', handleEnd);
  audio.play().catch(() => {});
  stopFn = () => {
    stopTick();
    audio.pause();
    audio.removeEventListener('timeupdate', handleTime);
    audio.removeEventListener('ended', handleEnd);
    currentAudio = null;
  };
  return stopFn;
}

// Skip windows for long inter-word silences (picture-book pauses inside an
// unpunctuated sentence): keep a natural 0.5s trail-off and 0.2s lead-in,
// jump the dead air between. Words must be in clip seconds.
export function gapSkips(words, minGap = 2.0, keepHead = 0.5, keepTail = 0.2) {
  const out = [];
  for (let i = 0; i + 1 < (words || []).length; i++) {
    const gap = words[i + 1].start - words[i].end;
    if (gap > minGap) out.push([words[i].end + keepHead, words[i + 1].start - keepTail]);
  }
  return out;
}

// --- Range playback: ONE reused <audio> element ---------------------------
// A fresh Audio() per range needs a user gesture for each .play() on iOS (and
// in Chrome's autoplay-strict states): play() fires after loadedmetadata,
// outside the tap's activation window, and the rejection is swallowed — so
// autoplay-on-advance worked only when the clip came from cache. Same fix
// playChain already carries: reuse one gesture-activated element, swapping src
// only when the clip changes. rangeToken is the per-call claim on the shared
// element (the double-play rule between two playRange calls); rangeDetach
// strips the previous call's listeners so they can't fire on the next range.
let rangeEl = null, rangeElUrl = null, rangeToken = null, rangeDetach = null;
function rangeElement(url) {
  if (rangeDetach) rangeDetach();
  if (!rangeEl) { rangeEl = new Audio(url); rangeElUrl = url; return rangeEl; }
  if (rangeElUrl !== url) {
    rangeEl.pause();
    rangeEl.src = url; rangeElUrl = url;               // same element: no new gesture needed
    rangeEl.load();
  }
  return rangeEl;
}

// Play [startTime, endTime] of a file. onTimeUpdate gets time relative to the
// (clamped) start. The seek is clamped to the clip so an out-of-range start
// never silently restarts from 0. `skips` = [[a,b], …] silent spans to jump.
export function playRange(url, startTime, endTime, onTimeUpdate, onEnded, preRoll = PRE_ROLL, postRoll = POST_ROLL, skips = []) {
  stopAll();
  const audio = rangeElement(url);
  const me = {};
  rangeToken = me;
  currentAudio = audio;
  const loadStop = () => { audio.pause(); currentAudio = null; };
  stopFn = loadStop;   // covers the metadata-loading window
  whenReady(audio, () => {
    // Superseded while loading (a newer playRange took the shared element), or
    // another clip is live — yield, but ALWAYS fire onEnded so the caller's
    // playing-state resets (a silent abort left ▶ buttons stuck "playing").
    if (rangeToken !== me) { onEnded && onEnded(); return; }
    if (!claimSlot(audio, loadStop)) { onEnded && onEnded(); return; }
    const dur = audio.duration || 0;
    const want = startTime ?? 0;
    const start = Math.max(0, Math.min(want - preRoll, Math.max(0, dur - 0.02)));   // lead-in
    const end = endTime != null ? Math.min(endTime + postRoll, dur) : null;          // tail-out
    audio.currentTime = start;
    let stopTick = () => {};
    const detach = () => {
      stopTick();
      audio.removeEventListener('timeupdate', checkEnd);
      audio.removeEventListener('ended', handleEnd);
      if (rangeDetach === detach) rangeDetach = null;
    };
    const checkEnd = () => {
      for (const [a, b] of skips) {
        if (audio.currentTime >= a && audio.currentTime < b) { audio.currentTime = b; return; }
      }
      if (end != null && audio.currentTime >= end) {
        detach();
        audio.pause();
        // Clear registration BEFORE onEnded: chained callers start the next
        // clip inside onEnded, and clearing after would clobber its stopFn.
        stopFn = null; currentAudio = null;
        onEnded && onEnded();
      } else {
        onTimeUpdate && onTimeUpdate(audio.currentTime - want);   // time relative to the ORIGINAL start
      }
    };
    // 'ended' always attached: a clip shorter than `end` must still clean the
    // shared element up (a stale listener here would fire into the NEXT range).
    const handleEnd = () => { detach(); stopFn = null; currentAudio = null; onEnded && onEnded(); };
    audio.addEventListener('timeupdate', checkEnd);
    stopTick = highlightTicker(() => {
      if (currentAudio !== audio || audio.paused) return;
      if (end != null && audio.currentTime >= end) return;   // let timeupdate's checkEnd advance
      onTimeUpdate && onTimeUpdate(audio.currentTime - want);
    });
    audio.addEventListener('ended', handleEnd);
    rangeDetach = detach;
    audio.play().catch(() => {});
    stopFn = () => {
      detach();
      audio.pause();
      currentAudio = null;
    };
  });
  return () => stopAll();
}

export function playSegment(url, startTime, endTime, onEnded) {
  return playRange(url, startTime, endTime, null, onEnded);
}

// Chained playback of [{url, start, end, preRoll, postRoll, skips}] on ONE
// <audio> element. A fresh Audio() per part needs a user gesture for each
// .play() on iOS (and in some Chrome autoplay states) — the rejection was
// silently swallowed and 段 died after its first sentence. Reusing the
// gesture-activated element (swapping src only when the clip changes) is the
// standard workaround, and gives seamless part joins within one clip for free.
export function playChain(items, onTimeUpdate, onItem, onDone) {
  stopAll();
  if (!items || !items.length) { onDone && onDone(); return () => {}; }
  const audio = new Audio(items[0].url);
  let idx = -1, it = null, curUrl = items[0].url;
  currentAudio = audio;
  const stopTick = highlightTicker(() => {
    if (currentAudio !== audio || audio.paused || !it) return;
    if (audio.currentTime >= endAt()) return;                // let timeupdate's checkEnd advance
    onTimeUpdate && onTimeUpdate(idx, audio.currentTime);
  });
  const detach = () => {
    stopTick();
    audio.removeEventListener('timeupdate', checkEnd);
    audio.removeEventListener('ended', handleEnd);
  };
  const chainStop = () => { detach(); audio.pause(); currentAudio = null; };
  stopFn = chainStop;
  const finish = () => { detach(); audio.pause(); stopFn = null; currentAudio = null; onDone && onDone(); };
  const begin = () => {
    if (!claimSlot(audio, chainStop)) { detach(); onDone && onDone(); return; }   // another clip is live — yield
    const dur = audio.duration || 0;
    const start = Math.max(0, Math.min((it.start ?? 0) - (it.preRoll ?? PRE_ROLL), Math.max(0, dur - 0.02)));
    audio.currentTime = start;
    onItem && onItem(idx);
    audio.play().catch(() => {});
  };
  const next = () => {
    idx += 1;
    if (idx >= items.length) { finish(); return; }
    it = items[idx];
    if (it.url !== curUrl) {
      curUrl = it.url;
      audio.pause();
      audio.src = it.url;                                  // same element: no new gesture needed
      audio.load();
      whenReady(audio, begin);
    } else {
      begin();                                             // same clip: just seek — seamless
    }
  };
  const endAt = () => {
    const dur = audio.duration || 0;
    return it.end != null ? Math.min(it.end + (it.postRoll ?? POST_ROLL), dur) : dur;
  };
  const checkEnd = () => {
    if (!it) return;
    for (const [a, b] of (it.skips || [])) {
      if (audio.currentTime >= a && audio.currentTime < b) { audio.currentTime = b; return; }
    }
    if (audio.currentTime >= endAt()) next();
    else onTimeUpdate && onTimeUpdate(idx, audio.currentTime);
  };
  // Clip ran out before it.end (short file). Guard staleness: after checkEnd
  // already advanced, a queued 'ended' from the old position must not advance
  // again (src swap → readyState 0; same-clip seek → currentTime back below end).
  const handleEnd = () => {
    if (audio.readyState === 0) return;
    if (it && audio.currentTime < endAt() - 0.05) return;
    next();
  };
  audio.addEventListener('timeupdate', checkEnd);
  audio.addEventListener('ended', handleEnd);
  whenReady(audio, () => next());
  return () => stopAll();
}

export function isPlaying() {
  return currentAudio != null && !currentAudio.paused;
}
